<div class="flex flex-col gap-8">
  <div class="flex flex-col gap-2">
    <div class="flex min-h-[50px] items-center gap-2 bg-slate-100 px-4">
      <mat-icon class="text-slate-500">storefront</mat-icon>
      <h3 class="!m-0 grow !font-bold text-slate-500">Merchants</h3>
    </div>

    <div class="flex flex-col gap-2 px-4">
      <div>
        Merchants are detected automatically from your HICAPS terminals, so
        there's nothing to add here. Run Sync to pick up any new ones.
      </div>
      <div>
        Give each merchant a name your team will recognise. Assigning a
        practitioner limits that merchant to their payments and claims,
        merchants left unassigned stay available to everyone.
      </div>
    </div>

    <div class="px-2">
      @if (hasMerchants$ | async) {
        <table class="w-full" mat-table [dataSource]="merchants$ | async">
          <ng-container matColumnDef="name">
            <th *matHeaderCellDef mat-header-cell>Name</th>
            <td *matCellDef="let merchant" mat-cell>
              <div class="flex items-center gap-2">
                <span class="font-bold" [class.opacity-50]="merchant.disabled">
                  {{ merchant | map: getMerchantDisplay }}
                </span>
                @if (merchant.disabled) {
                  <span
                    class="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600"
                    matTooltip="This merchant won't be offered when taking payments"
                  >
                    Disabled
                  </span>
                }
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="merchantId">
            <th *matHeaderCellDef mat-header-cell>Merchant ID</th>
            <td *matCellDef="let merchant" mat-cell>
              {{ merchant.merchantId }}
            </td>
          </ng-container>

          <ng-container matColumnDef="providerName">
            <th *matHeaderCellDef mat-header-cell>Provider Name</th>
            <td *matCellDef="let merchant" mat-cell>
              {{ merchant.providerName }}
            </td>
          </ng-container>

          <ng-container matColumnDef="practitioner">
            <th *matHeaderCellDef mat-header-cell>Practitioner</th>
            <td *matCellDef="let merchant" mat-cell>
              @if (merchant.practitionerRef) {
                @if (
                  merchant.practitionerRef
                    | map: globalStore.getStaffer$ : globalStore
                    | async;
                  as staffer
                ) {
                  <pr-staffer-display [staffer]="staffer" />
                }
              } @else {
                <span class="mat-caption opacity-50">None</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th *matHeaderCellDef mat-header-cell></th>
            <td class="!text-right" *matCellDef="let merchant" mat-cell>
              <button
                mat-icon-button
                aria-label="Merchant actions"
                [matMenuTriggerFor]="merchantMenu"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #merchantMenu="matMenu">
                <ng-template matMenuContent>
                  <button
                    mat-menu-item
                    (click)="openEditMerchantDialog(merchant)"
                  >
                    <mat-icon color="accent">edit</mat-icon>
                    <span>Edit</span>
                  </button>
                  @if (merchant.disabled) {
                    <button
                      mat-menu-item
                      (click)="toggleMerchantDisabled(merchant, false)"
                    >
                      <mat-icon color="accent">check_circle</mat-icon>
                      <span>Enable</span>
                    </button>
                  } @else {
                    <button
                      mat-menu-item
                      (click)="toggleMerchantDisabled(merchant, true)"
                    >
                      <mat-icon color="warn">block</mat-icon>
                      <span>Disable</span>
                    </button>
                  }
                </ng-template>
              </mat-menu>
            </td>
          </ng-container>

          <tr
            *matHeaderRowDef="merchantColumns"
            mat-header-row
            class="opacity-50"
          ></tr>
          <tr *matRowDef="let row; columns: merchantColumns" mat-row></tr>
        </table>
      } @else {
        <div class="p-4 text-slate-400">
          No merchants detected yet. Run Sync to look for them.
        </div>
      }
    </div>
  </div>

  <div class="flex flex-col gap-2">
    <div class="flex min-h-[50px] items-center gap-2 bg-slate-100 px-4">
      <mat-icon class="text-slate-500">devices</mat-icon>
      <h3 class="!m-0 grow !font-bold text-slate-500">Terminals</h3>
      <pt-loader-button
        type="flat"
        color="primary"
        [loading]="isSyncing()"
        (clicked)="syncAll(practice())"
      >
        Sync
      </pt-loader-button>
    </div>

    <div class="flex flex-col gap-2 px-4">
      <div>
        Terminals are found through Principle Bridge, which needs to be
        installed on a computer in your practice. Run Sync to pick up any new
        ones.
      </div>
      <div>
        Give each terminal a name your team will recognise. When a terminal can
        be reached by more than one bridge, you can pin the one it should use,
        otherwise the best available connection is chosen for you.
      </div>
    </div>

    <div class="px-2">
      @if (bridges$ | async; as bridges) {
        @if (merchants$ | async; as merchants) {
          @if (hasTerminalsWithMerchants$ | async) {
            <table
              class="w-full"
              mat-table
              [dataSource]="terminalsWithMerchants$ | async"
            >
              <ng-container matColumnDef="name">
                <th *matHeaderCellDef mat-header-cell>Name</th>
                <td *matCellDef="let terminal" mat-cell>
                  <div class="flex items-center gap-2">
                    <span
                      class="font-bold"
                      [class.opacity-50]="terminal.isDisabled"
                      >{{ terminal.name }}</span
                    >
                    @if (terminal.isDisabled) {
                      <span
                        class="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600"
                        matTooltip="This terminal won't be offered when taking payments"
                      >
                        Disabled
                      </span>
                    }
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="terminalId">
                <th *matHeaderCellDef mat-header-cell>Terminal ID</th>
                <td *matCellDef="let terminal" mat-cell>
                  <span class="text-xs text-gray-500">{{
                    terminal.terminalId
                  }}</span>
                </td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th *matHeaderCellDef mat-header-cell>Status</th>
                <td *matCellDef="let terminal" mat-cell>
                  <pr-status-label
                    [dense]="true"
                    [label]="terminal.isActive ? 'Active' : 'Inactive'"
                    [color]="terminal.isActive ? 'success' : 'warn'"
                  />
                </td>
              </ng-container>

              <ng-container matColumnDef="servingBridge">
                <th *matHeaderCellDef mat-header-cell>Serving Bridge</th>
                <td *matCellDef="let terminal" mat-cell>
                  @if (terminal.servingBridge) {
                    <div class="flex items-center gap-2">
                      @if (terminal.usedFallback) {
                        <span class="font-bold text-orange-600">
                          ⚠ preferred offline, using
                          {{ terminal.servingBridge }}
                        </span>
                      } @else {
                        <span class="text-gray-500">
                          {{ terminal.servingBridge }}
                        </span>
                      }
                      <span
                        class="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600"
                        [matTooltip]="
                          terminal.preferredBridge
                            ? 'A preferred bridge is pinned for this terminal'
                            : 'No preferred bridge set — routing picks the best available'
                        "
                      >
                        {{ terminal.preferredBridge ? 'Pinned' : 'Auto' }}
                      </span>
                    </div>
                  }
                </td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th *matHeaderCellDef mat-header-cell></th>
                <td *matCellDef="let terminal" mat-cell class="!text-right">
                  <button
                    mat-icon-button
                    aria-label="Terminal actions"
                    [matMenuTriggerFor]="moreMenu"
                  >
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #moreMenu="matMenu">
                    <ng-template matMenuContent>
                      <button
                        mat-menu-item
                        (click)="
                          openEditTerminalDialog(terminal, bridges, merchants)
                        "
                      >
                        <mat-icon color="accent">edit</mat-icon>
                        <span>Edit</span>
                      </button>
                      <span
                        matTooltip="Terminal has no active route"
                        [matTooltipDisabled]="terminal.isActive"
                      >
                        <button
                          mat-menu-item
                          [disabled]="!terminal.isActive"
                          (click)="testTerminal(terminal, bridges, merchants)"
                        >
                          <mat-icon color="accent">sync</mat-icon>
                          <span>Test Terminal</span>
                        </button>
                      </span>
                      <span
                        matTooltip="Terminal has no active route"
                        [matTooltipDisabled]="terminal.isActive"
                      >
                        <button
                          mat-menu-item
                          [disabled]="!terminal.isActive"
                          (click)="
                            viewLogsForTerminal(terminal, bridges, merchants)
                          "
                        >
                          <mat-icon color="accent">receipt_long</mat-icon>
                          <span>View Logs</span>
                        </button>
                      </span>
                      @if (terminal.isDisabled) {
                        <button
                          mat-menu-item
                          (click)="enableTerminal(terminal)"
                        >
                          <mat-icon color="accent">check_circle</mat-icon>
                          <span>Enable</span>
                        </button>
                      } @else {
                        <button
                          mat-menu-item
                          (click)="disableTerminal(terminal)"
                        >
                          <mat-icon color="warn">block</mat-icon>
                          <span>Disable</span>
                        </button>
                      }
                    </ng-template>
                  </mat-menu>
                </td>
              </ng-container>

              <tr *matHeaderRowDef="terminalColumns" mat-header-row></tr>
              <tr *matRowDef="let row; columns: terminalColumns" mat-row></tr>
            </table>
          } @else {
            <div class="p-4 text-slate-400">
              No terminals detected yet. Run Sync to look for them.
            </div>
          }
        }
      }
    </div>
  </div>
</div>
